import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Bot, Zap, Bug, Settings, AlertCircle, Sparkles, PlayCircle, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../Layout/MainLayout';
import ChatHeader from '../ChatCore/ChatHeader';
import V2ChatMessages from '../V2Chat/V2ChatMessages';
import MessageInput from '../ChatCore/MessageInput';
import UniversalStepManager from '../Workflows/UniversalStepManager';
import { useAuth } from '../../context/AuthContext';
import GenericChatService from '../../services/GenericChatService';

const SankalvaXChat = () => {
  const { sessionId: urlSessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // URL parameters
  const workflowId = searchParams.get('workflow');
  const workflowName = searchParams.get('workflowName');
  const executionId = searchParams.get('execution');
  const version = searchParams.get('version'); // NEW: Check for version parameter
  const mode = searchParams.get('mode') || (workflowId ? 'workflow' : 'direct');
  const agentId = searchParams.get('agentId'); // Get agentId for back navigation

  // State management
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(urlSessionId || searchParams.get('sessionId'));
  const [sessionName, setSessionName] = useState('SankalvaX AI');
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [totalTokens, setTotalTokens] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [chatService, setChatService] = useState(null);
  const [isWorkflowMode, setIsWorkflowMode] = useState(mode === 'workflow');
  const [isV2Workflow, setIsV2Workflow] = useState(version === 'v2'); // NEW: Track V2 workflow state
  const [isDisabled, setIsDisabled] = useState(false);
  const [draftData, setDraftData] = useState(null);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [resumeTrigger, setResumeTrigger] = useState(0);
  const [isResuming, setIsResuming] = useState(false);
  const [sessionStatus, setSessionStatus] = useState(null); // Track if session is 'draft', 'completed', etc.
  const [workflowRunStatus, setWorkflowRunStatus] = useState(null); // Track workflow run status (completed, awaiting_user, etc.)

  // Initialize chat service
  useEffect(() => {
    if (token) {
      const service = new GenericChatService(token);
      setChatService(service);

      // Subscribe to events
      const unsubscribeMessage = service.onMessage(handleServiceMessage);
      const unsubscribeStatus = service.onStatusChange(setConnectionStatus);

      return () => {
        unsubscribeMessage();
        unsubscribeStatus();
        service.destroy();
      };
    }
  }, [token]);

  // Handle service messages
  const handleServiceMessage = useCallback((data) => {
    if (data.type === 'new_message') {
      // Skip user messages - they're already added immediately when sent
      if (data.message.type === 'user') {
        console.log('⏭️ FRONTEND DEBUG: Skipping user message from service (already displayed):', data.message);
        return;
      }

      setMessages(prev => [...prev, data.message]);
      setIsLoading(false);

      // Update metrics
      if (data.message.metadata?.tokens_used) {
        setTotalTokens(prev => prev + data.message.metadata.tokens_used);
      }
      if (data.message.metadata?.cost) {
        setTotalCost(prev => prev + data.message.metadata.cost);
      }
    } else if (data.type === 'error') {
      console.error('Chat error:', data.error);
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Error: ${data.error}`,
        timestamp: new Date().toISOString()
      }]);
      setIsLoading(false);
    }
  }, []);

  // Initialize session based on mode
  useEffect(() => {
    const initializeSession = async () => {
      if (!chatService) return;

      if (mode === 'direct' && !workflowId) {
        // Direct access without workflow - show disabled state
        setIsDisabled(true);
        setSessionName('SankalvaX AI - Direct Chat');
        setMessages([{
          id: 'disabled-notice',
          type: 'system',
          content: '🚧 Direct SankalvaX AI chat is currently under development. Please use the Chat Playground for now, or start a conversation through a workflow.',
          timestamp: new Date().toISOString()
        }]);
        return;
      }

      if (workflowId && workflowName) {
        // Workflow mode - now using V2 APIs by default
        const isV2 = version === 'v2' || true; // Default to V2 for all workflows
        setIsWorkflowMode(true);
        setIsV2Workflow(isV2);
        setSessionName(`SankalvaX AI - ${workflowName}`); // No V2 indicator in UI

        // Configure chat service for V2
        if (chatService && isV2) {
          chatService.setV2Workflow(true, workflowId);
        }
        
        if (sessionId) {
          // Try to load existing session first
          try {
            await loadExistingSession(sessionId);
          } catch (error) {
            console.log('Session not found, will create new one on first message');
            setMessages([{
              id: 'workflow-welcome',
              type: 'system',
              content: `🚀 Workflow "${workflowName}" is ready! Send a message to start the workflow execution.`,
              timestamp: new Date().toISOString()
            }]);
          }
        } else {
          // No session ID, will create on first message
          setMessages([{
            id: 'workflow-welcome',
            type: 'system',
            content: `🚀 Workflow "${workflowName}" is ready! Send a message to start the workflow execution.`,
            timestamp: new Date().toISOString()
          }]);
        }
      } else if (sessionId) {
        // Existing session
        await loadExistingSession(sessionId);
      }
    };

    initializeSession();
  }, [chatService, sessionId, workflowId, workflowName, mode, version]);

  // Load existing session
  const loadExistingSession = async (sessionIdToLoad) => {
    if (!chatService) return;

    try {
      console.log('Loading session:', sessionIdToLoad);

      // Get session details to determine if it's a workflow session
      const sessionDetails = await chatService.getSessionDetails(sessionIdToLoad);
      if (sessionDetails) {
        console.log('Session details:', sessionDetails);

        // Track session status to control UI elements (Review button should not show for completed workflows)
        setSessionStatus(sessionDetails.session_status);
        console.log('📊 Session status set to:', sessionDetails.session_status);

        // Check for draft data - only if session status is 'draft', not 'completed'
        if (sessionDetails.draft_data && sessionDetails.session_status === 'draft') {
          setDraftData(sessionDetails.draft_data);
          console.log('📝 Found draft data for session:', sessionDetails.draft_data);
        } else if (sessionDetails.draft_data && sessionDetails.session_status === 'completed') {
          console.log('✅ Session was a draft but is now completed - not showing Resume button');
          setDraftData(null);
        }

        // Update state based on session metadata
        if (sessionDetails.is_workflow_chat && sessionDetails.workflow_id) {
          setIsWorkflowMode(true);
          setIsV2Workflow(true); // Assume V2 for existing workflow sessions

          // Use run_id from chat_sessions for debug navigation
          if (sessionDetails.run_id) {
            chatService.v2RunId = sessionDetails.run_id;
            console.log('✅ FRONTEND DEBUG: Set run_id for debug navigation:', sessionDetails.run_id);

            // 🔧 Fetch workflow run status to prevent modal reopening on completed workflows
            try {
              const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
              const runStatusResponse = await fetch(`${API_BASE_URL}/api/v2/workflows/run/${sessionDetails.run_id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (runStatusResponse.ok) {
                const runData = await runStatusResponse.json();
                setWorkflowRunStatus(runData.run_status);
                console.log('✅ Fetched workflow run status:', runData.run_status);
              }
            } catch (err) {
              console.error('⚠️ Could not fetch workflow run status:', err);
            }
          }

          // Update URL params if not already set
          if (!workflowId) {
            const currentUrl = new URL(window.location);
            currentUrl.searchParams.set('workflow', sessionDetails.workflow_id);
            currentUrl.searchParams.set('workflowName', sessionDetails.workflow_name || '');
            currentUrl.searchParams.set('version', 'v2');
            currentUrl.searchParams.set('mode', 'workflow');
            window.history.replaceState(null, '', currentUrl.toString());
          }
        }
      }

      const sessionMessages = await chatService.loadSession(sessionIdToLoad);

      // Initialize message count for V2 workflow continuation
      if (sessionDetails?.is_workflow_chat && chatService.v2RunId) {
        chatService.v2MessageCount = sessionMessages.length;
        console.log('📊 FRONTEND DEBUG: Initialized v2MessageCount for continued session:', sessionMessages.length);
      }

      // Sort messages by timestamp to ensure chronological order
      const sortedMessages = [...sessionMessages].sort((a, b) => {
        const timeA = new Date(a.timestamp).getTime();
        const timeB = new Date(b.timestamp).getTime();
        return timeA - timeB;
      });
      console.log('📊 FRONTEND DEBUG: Sorted messages by timestamp for history display');

      setMessages(sortedMessages);
      setSessionId(sessionIdToLoad);

      // Set connection status to connected (WebSocket removed)
      setConnectionStatus('connected');

      // Add continuation message
      if (sessionMessages.length > 0) {
        setMessages(prev => [...prev, {
          id: 'session-resumed',
          type: 'system',
          content: '📋 Conversation resumed. Continue where you left off!',
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      setMessages([{
        id: 'session-error',
        type: 'system',
        content: '⚠️ Could not load conversation. Starting fresh.',
        timestamp: new Date().toISOString()
      }]);
    }
  };

  // V2 workflows don't need session creation - they use run_id directly
  // OLD V1 SESSION CREATION - COMMENTED OUT FOR REFERENCE
  /*
  const createWorkflowSession = async () => {
    if (!workflowId || !token) return null;

    try {
      console.log('🎯 FRONTEND DEBUG: Creating workflow session', { workflowId, workflowName });

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai'}/api/chat/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_name: `Workflow: ${workflowName || 'Unnamed'}`,
          workflow_id: String(workflowId)
        })
      });

      if (response.ok) {
        const sessionData = await response.json();
        console.log('✅ FRONTEND DEBUG: Workflow session created', sessionData);

        const newSessionId = sessionData.session_id;
        setSessionId(newSessionId);

        // Set connection status to connected (WebSocket removed)
        setConnectionStatus('connected');

        const newUrl = `/chat/${newSessionId}?workflow=${workflowId}&workflowName=${encodeURIComponent(workflowName || '')}&mode=workflow`;
        window.history.replaceState(null, '', newUrl);

        return newSessionId;
      } else {
        console.error('Failed to create workflow session:', response.status);
        return null;
      }
    } catch (error) {
      console.error('Error creating workflow session:', error);
      return null;
    }
  };
  */

  // 🔧 Refetch workflow run status on component mount (handles browser back/forward navigation)
  // This ensures executionStatus is always available, even when component remounts with same sessionId
  useEffect(() => {
    const fetchWorkflowStatus = async () => {
      // Only fetch if we don't have status but we do have a session with run_id
      if (workflowRunStatus !== null) return; // Already have status
      if (!sessionId || !token || !chatService) return;

      try {
        const sessionDetails = await chatService.getSessionDetails(sessionId);
        if (sessionDetails?.run_id) {
          const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
          const runStatusResponse = await fetch(`${API_BASE_URL}/api/v2/workflows/run/${sessionDetails.run_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (runStatusResponse.ok) {
            const runData = await runStatusResponse.json();
            setWorkflowRunStatus(runData.run_status);
            console.log('🔄 Refetched workflow run status on mount:', runData.run_status);
          }
        }
      } catch (err) {
        console.error('⚠️ Could not refetch workflow run status:', err);
      }
    };

    fetchWorkflowStatus();
  }, [sessionId, token, chatService, workflowRunStatus]);

  // Send message - updated for V2 workflows
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !chatService || isDisabled) return;

    setIsLoading(true);

    // Add user message immediately
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');

    try {
      if (isWorkflowMode && workflowId && isV2Workflow) {
        // V2 workflow execution - pass existing sessionId to continue in same chat
        await sendWorkflowMessage(sessionId, messageToSend);
      } else if (isWorkflowMode && workflowId) {
        // V1 workflow (deprecated but kept for reference)
        throw new Error('V1 workflows are deprecated. Please use V2.');
      } else {
        // Regular chat - need session ID
        if (!sessionId) {
          throw new Error('No session available for regular chat');
        }
        await chatService.sendMessage(sessionId, messageToSend);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Error: ${error.message}`,
        timestamp: new Date().toISOString()
      }]);
      setIsLoading(false);
    }
  };

  // Send workflow message using V2 APIs
  const sendWorkflowMessage = async (sessionId, message) => {
    console.log('🌐 FRONTEND DEBUG: Sending V2 workflow message', {
      sessionId,
      workflowId,
      message,
      isV2Workflow
    });

    if (isV2Workflow) {
      // Use V2 workflow execution
      try {
        if (!chatService.getV2RunId()) {
          // Start new V2 workflow - pass existing sessionId to continue in same chat
          const result = await chatService.executeWorkflowV2(workflowId, message, sessionId);
          console.log('✅ V2 Workflow started:', result);
        } else {
          // Continue existing V2 workflow
          await chatService.continueWorkflowV2(message);
          console.log('✅ V2 Workflow continued');
        }
      } catch (error) {
        console.error('❌ V2 Workflow execution failed:', error);
        throw error;
      }
    } else {
      // OLD V1 CODE - COMMENTED OUT FOR REFERENCE
      /*
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai'}/api/chat/workflows/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: sessionId,
          workflow_id: String(workflowId),
          user_message: message,
          provider: 'openai',
          model: 'gpt-4o'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ FRONTEND DEBUG: V1 Workflow execution failed', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`Failed to execute workflow: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ FRONTEND DEBUG: V1 Workflow execution response', result);
      */
      throw new Error('V1 workflows are deprecated. Please use V2.');
    }
  };

  // Handle debug button
  const handleOpenDebug = () => {
    if (isV2Workflow && chatService?.getV2RunId()) {
      // V2 workflow - use run_id for debugging
      navigate(`/debug?execution=${chatService.getV2RunId()}&type=v2`);
    } else if (sessionId) {
      // Regular session debugging
      navigate(`/debug?execution=${sessionId}&type=v2`);
    } else if (executionId) {
      // Legacy execution debugging
      navigate(`/debug?execution=${executionId}&type=v2`);
    }
  };

  // Enhanced header with SankalvaX branding
  // Check if there's a PreBillCreationModal message in the conversation
  const hasBillDataInMessages = () => {
    // First check if bill was already created
    const billAlreadyCreated = messages.some(msg => {
      try {
        let content = msg.content;
        if (typeof content === 'string') {
          const jsonMatch = content.match(/\{.*\}/s);
          if (jsonMatch) {
            content = JSON.parse(jsonMatch[0]);
          } else {
            return content.includes('Bill created successfully') ||
                   content.includes('Internal ID:') ||
                   content.includes('bill_creation_successful') ||
                   content.includes('netsuite_url'); // ✅ Check for NetSuite URL in success message
          }
        }
        if (typeof content === 'object' && content !== null) {
          return content.next_action_required === 'bill_creation_successful' ||
                 content.netsuite_url || // ✅ Check for netsuite_url field
                 (content.message_for_user && content.message_for_user.includes('Bill created successfully'));
        }
      } catch (e) {
        return false;
      }
      return false;
    });

    // If bill already created, don't show Review button
    if (billAlreadyCreated) {
      console.log('🚫 hasBillDataInMessages: Bill already created - hiding Review button');
      return false;
    }

    // Check if there's a PreBillCreationModal (pending review)
    return messages.some(msg => {
      try {
        let content = msg.content;

        // Parse string content if needed
        if (typeof content === 'string') {
          const trimmed = content.trim();
          if (trimmed.startsWith('{')) {
            content = JSON.parse(trimmed);
          }
        }

        // Check for PreBillCreationModal component
        // ✅ Non-PO (flat) bills: bill_data spread into component_definition with top-level line_items
        // ✅ PO-linked bills: items live under po_data.line_items / original_pdf_line_items (no top-level line_items)
        const cd = content?.component_definition;
        return cd?.metadata?.name === 'PreBillCreationModal' &&
               (cd.line_items || cd.po_data || cd.original_pdf_line_items);
      } catch (e) {
        return false;
      }
    });
  };

  const renderCustomHeader = () => (
    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800">
      <div className="flex items-center space-x-4">
        {/* Back Button */}
        <button
          onClick={() => agentId ? navigate(`/threads?agentId=${agentId}`) : navigate('/threads')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title="Back to Threads"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
        <div className="relative">
          <div className="p-3 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl shadow-lg">
            <Bot className="w-8 h-8 text-white" />
          </div>
          <div className="absolute -bottom-1 -right-1 p-1 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full">
            <Sparkles className="w-3 h-3 text-white" />
          </div>
        </div>
        <div>
          <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            {sessionName}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {isWorkflowMode ? 'Workflow Assistant' : 'AI Conversation'}
            {isDisabled && ' (Coming Soon)'}
          </p>
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        {!isDisabled && (
          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center">
              <Zap className="w-3 h-3 mr-1" />
              {totalTokens} tokens
            </span>
            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
              connectionStatus === 'connected' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' : 'bg-red-500'
              }`} />
              <span>{connectionStatus}</span>
            </div>
          </div>
        )}

        {isWorkflowMode && workflowId && sessionStatus !== 'completed' && (draftData || hasBillDataInMessages()) && (
          <button
            onClick={() => {
              console.log('🔵 Review button clicked. workflowId:', workflowId, 'draftData:', draftData, 'sessionStatus:', sessionStatus);

              // If no draftData, extract bill data from messages
              if (!draftData) {
                const billDataMsg = messages.find(msg => {
                  try {
                    let content = msg.content;
                    if (typeof content === 'string') {
                      const trimmed = content.trim();
                      if (trimmed.startsWith('{')) {
                        content = JSON.parse(trimmed);
                      }
                    }
                    return content?.component_definition?.metadata?.name === 'PreBillCreationModal';
                  } catch (e) {
                    return false;
                  }
                });

                if (billDataMsg) {
                  let content = billDataMsg.content;
                  if (typeof content === 'string') {
                    content = JSON.parse(content.trim());
                  }
                  // ✅ FLAT structure - bill data is directly in component_definition
                  const extractedBillData = content.component_definition;

                  // 🔧 FIX: Clean up malformed expense data (double-nested account.id from old bills)
                  if (extractedBillData.expense && extractedBillData.expense.items) {
                    extractedBillData.expense.items = extractedBillData.expense.items.map(exp => {
                      // Fix double-nested account.id: {id: {id: ""}} -> {id: ""}
                      if (exp.account && typeof exp.account.id === 'object' && exp.account.id !== null) {
                        return {
                          ...exp,
                          account: { id: exp.account.id.id || '' }
                        };
                      }
                      return exp;
                    });

                    console.log('🔧 Cleaned expense data:', extractedBillData.expense);
                  }

                  // CRITICAL: Include runId from chatService or sessionId
                  const runIdValue = chatService?.v2RunId || sessionId;
                  const billDataWithRunId = {
                    ...extractedBillData,
                    runId: runIdValue
                  };

                  console.log('🔵 Extracted bill data from messages:', billDataWithRunId);
                  setDraftData(billDataWithRunId);
                  // Give React a moment to update draftData state before triggering
                  setTimeout(() => {
                    setIsResuming(true);
                    setResumeTrigger(prev => prev + 1);
                  }, 50);
                  return;
                }
              }

              setIsResuming(true);
              setResumeTrigger(prev => prev + 1);
            }}
            disabled={isResuming}
            className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            title={draftData ? "Review Draft" : "Review Bill"}
          >
            <PlayCircle className="w-4 h-4" />
            <span className="text-sm font-medium">{isResuming ? 'Reviewing...' : 'Review'}</span>
          </button>
        )}

        {isAdmin && (sessionId || (isV2Workflow && chatService?.getV2RunId())) && (
          <button
            onClick={handleOpenDebug}
            className="p-2 text-purple-500 hover:text-purple-600 dark:hover:text-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
            title="Debug This Conversation"
          >
            <Bug className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <MainLayout title="SankalvaX AI Chat">
      <div className="flex flex-col h-[calc(100vh-8rem)] bg-white dark:bg-gray-900">
        {/* Custom Header */}
        {renderCustomHeader()}

        {/* Messages */}
        {(() => {
          const runIdValue = draftData?.runId || chatService?.v2RunId || sessionId;
          console.log('🔍 DEBUG: Passing runId to V2ChatMessages:', {
            draftDataRunId: draftData?.runId,
            chatServiceRunId: chatService?.v2RunId,
            sessionId: sessionId,
            finalRunId: runIdValue
          });
          return (
            <V2ChatMessages
              messages={messages}
              isLoading={isLoading}
              workflowName={workflowName || "SankalvaX AI"}
              error={null}
              draftBillData={draftData}
              resumeTrigger={resumeTrigger}
              runId={runIdValue}
              workflowId={workflowId}
              token={token}
              sendMessage={handleSendMessage}
              chatService={chatService}
              isResuming={isResuming}
              setIsResuming={setIsResuming}
              executionStatus={workflowRunStatus}  // 🔧 Pass workflow run status to prevent modal reopening
              isReopenedSession={!!urlSessionId}  // Flag to prevent modal auto-open on reopened threads
              disableAutoOpen={true}  // 🔧 Disable auto-open for chat threads - modal only opens via Review button
              returnPath={agentId ? `/threads?agentId=${agentId}` : '/threads'}  // Redirect to threads page on cancel
            />
          );
        })()}

        {/* Input */}
        <MessageInput
          value={inputMessage}
          onChange={setInputMessage}
          onSend={handleSendMessage}
          isLoading={isLoading || isDisabled}
          placeholder={
            isDisabled
              ? "Direct chat coming soon - use Chat Playground for now"
              : isWorkflowMode
                ? `Message ${workflowName}...`
                : "Message SankalvaX AI..."
          }
          rows={2}
        />

        {isDisabled && (
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center justify-center space-x-2 text-sm text-yellow-800 dark:text-yellow-200">
              <AlertCircle className="w-4 h-4" />
              <span>Direct SankalvaX AI chat is under development. Use workflows or Chat Playground.</span>
            </div>
          </div>
        )}
      </div>

      {/* Resume Workflow Modal - Outside main container for proper overlay */}
      {showWorkflowModal && workflowId && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => {
            console.log('🔴 Modal backdrop clicked, closing modal');
            setShowWorkflowModal(false);
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-auto"
            onClick={(e) => {
              console.log('🟢 Modal content clicked, preventing close');
              e.stopPropagation();
            }}
          >
            {console.log('🟣 Rendering UniversalStepManager with workflowId:', workflowId, 'runId:', draftData?.runId)}
            <UniversalStepManager
              workflowId={workflowId}
              initialRunId={draftData?.runId || null}
              onChatRedirect={() => {
                console.log('✅ Workflow redirecting to chat, closing modal');
                setShowWorkflowModal(false);
                setDraftData(null);
              }}
              onWorkflowComplete={() => {
                console.log('✅ Workflow completed, closing modal');
                setShowWorkflowModal(false);
                setDraftData(null);
              }}
              onError={(error) => {
                console.error('❌ Workflow error:', error);
              }}
            />
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default SankalvaXChat;